import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
} from "@mui/material";

import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import { Add, Delete, Download, FilterList, MoreVert, Search, FirstPage, LastPage, Close, PhotoCamera, Remove } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { TrashOutline } from "react-ionicons";
import "../../../shared/components/Item.css";
import MapPinLocation from "../../../shared/components/MapPinLocation.tsx";

import { useNavigate } from "react-router-dom";

import { OfficeProjectSearchParams, getOfficeProjects, createOfficeProject, uploadOfficePhoto, editOfficeProject, fetchOfficePhotoBlob, deleteOfficeProject, getOfficeLocations, getAllOffices } from "../services/projectDetailServices.tsx";
import { LocationSearchParams, getLocations, createLocation, editLocation, deleteLocation } from "../../data_management/services/locationService.tsx";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import LoadingOverlay from "../../../shared/components/LoadingOverlay.tsx";

// Dummy Data
const officeTypes = ["PST", "MB", "PJTK", "KON", "SK"];

const officeTypeColors = {
  PST: "green",
  MB: "lightgreen",
  PJTK: "	#164396",
  KON: "gray",
  SK: "orange",
};

const regions = ["Jamali", "Sulmapana", "Sumkal"];

// Grafana constants
const GRAFANA_BASE = "https://103.23.30.133:3030";
const DASHBOARD_UID = "f09f938b";
const DASHBOARD_SLUG = "survey-kepuasan-pelanggan";

const BuildingSurvey: React.FC = () => {
  const grafanaChartRef = useRef<HTMLDivElement>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // Total semua data
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddOfficeDialog, setOpenAddOfficeDialog] = useState(false);
  const [openAddItemDetailsDialog, setOpenAddItemDetailsDialog] = useState(false);

  const [error, setError] = useState("");
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filters, setFilters] = useState([{ field: "name", value: "" }]);

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [officeOptions, setOfficeOptions] = useState<any[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<any[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [graphDate, setGraphDate] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });

  const [graphUrl, setGraphUrl] = useState("");

  const offset = (currentPage - 1) * rowsPerPage;

  const [sortConfig, setSortConfig] = useState<{ orderBy: string; order: "asc" | "desc" }>({
    orderBy: "createdAt",
    order: "desc",
  });

  const showPopupMandatoryError = () => {
    Swal.fire({
      icon: "error",
      title: "Data Belum Lengkap",
      text: "Lengkapi data yang wajib diisi.",
    });
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (prev.orderBy === column) {
        return {
          orderBy: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          orderBy: column,
          order: "asc",
        };
      }
    });
    setCurrentPage(1); // reset ke halaman 1 saat sort berubah
  };

  // Daftar pilihan filter yang tersedia
  const availableFilters = [
    { value: "officeName", label: "Office Name" },
    { value: "officeAddress", label: "Office Address" },
    { value: "officeEmail", label: "Office Email" },
    { value: "officeType", label: "Office Type" },
    { value: "region", label: "Region" },
  ];

  // Daftar pilihan filter yang tersedia
  const availableFiltersInit = [
    { value: "officeName", label: "Office Name" },
    { value: "officeAddress", label: "Office Address" },
    { value: "officeEmail", label: "Office Email" },
    { value: "officeType", label: "Office Type" },
    { value: "region", label: "Region" },
  ];

  const [filterParams, setFilterParams] = useState<{
    keyword?: string;
    officeName?: string;
    officeAddress?: string;
    region?: string;
    officeType?: string;
    officeEmail?: string;
    managerName?: string;
    contactNumber?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    orderBy?: string;
    order?: string;
  }>({});

  const displayedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Grafana URL builder
  const buildLiveUrl = (fromMs: number, toMs: number) => {
    const params = `orgId=1&from=${fromMs}&to=${toMs}&theme=light&kiosk&tz=Asia%2FJakarta`;
    return `https://103.23.30.133:3030/d/survey-kepuasan-pelanggan/f09f938b-survey-kepuasan-pelanggan?${params}?${params}`;
  };

  // Fetch on mount, default 12 months
  useEffect(() => {
    setIsLoading(true);
    const now = dayjs().endOf("day").valueOf();
    const back12m = dayjs().subtract(12, "month").startOf("day").valueOf();
    setGraphUrl(buildLiveUrl(back12m, now));
    setIsLoading(false);
  }, [currentPage, rowsPerPage, filterParams, sortConfig]);

  return (
    <>
      {isLoading ? <LoadingOverlay /> : null}
      <Box sx={{ padding: 3 }}>
        {/* Header & Tabs */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Building Survey
          </Typography>
        </Box>

        {/* Toolbar */}
        <Box className="toolbar">
          {/* Left Controls */}
          <Box className="leftControls">
            <Typography>Select Time Range</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Start Date"
                format="YYYY-MM-DD"
                value={startDate ? dayjs(startDate) : null}
                onChange={(newValue) => setStartDate(newValue ? newValue.format("YYYY-MM-DD") : null)}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                    sx: { mt: 1, minWidth: 150 },
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="End Date"
                format="YYYY-MM-DD"
                value={endDate ? dayjs(endDate) : null}
                onChange={(newValue) => setEndDate(newValue ? newValue.format("YYYY-MM-DD") : null)}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                    sx: { mt: 1, minWidth: 150 },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={() => {
                if (!startDate || !endDate) {
                  Swal.fire("Tanggal belum lengkap", "Pilih tanggal mulai dan akhir.", "warning");
                  return;
                }

                const startUnix = dayjs(startDate).startOf("day").valueOf();
                const endUnix = dayjs(endDate).endOf("day").valueOf();

                if (startUnix > endUnix) {
                  Swal.fire("Rentang tidak valid", "Tanggal mulai harus sebelum tanggal akhir.", "error");
                  return;
                }

                setIsLoading(true);
                setGraphUrl(buildLiveUrl(startUnix, endUnix));
                setIsLoading(false);
              }}
              className="searchButton"
            >
              Search
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 5 }} ref={grafanaChartRef}>
          {graphUrl && (
            <iframe
              src={graphUrl}
              title="Grafana Dashboard"
              style={{ width: "100%", height: "900px", border: 0 }}
              referrerPolicy="no-referrer"
              onLoad={() => {
                setIsLoading(false);
              }}
              onError={(e) => {
                console.error("Grafana failed to load", e);
                setIsLoading(false);
                Swal.fire(
                  "Gagal memuat dashboard",
                  "Periksa anonymous access Grafana atau atur reverse proxy agar X-Frame-Options memperbolehkan embed.",
                  "error"
                );
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default BuildingSurvey;
